import { useState } from 'react';
import { Modal, Alert, Input, Typography, Button } from '../../ui';
import { PALETTE } from '../../lib/palette';

export type CancelBreakdown = { ordered: number; packed: number; dispatched: number; delivered: number; pending: number };

type Props = {
  open: boolean;
  onClose: () => void;
  orderNo: string;
  breakdown: CancelBreakdown;
  fmtQty: (n: any) => string;
  /** No goods have shipped out yet → the whole order can still be withdrawn. */
  canFullCancel: boolean;
  /** Nothing shipped, but a recorded Tally invoice blocks the withdrawal until it's voided. */
  fullCancelBlockedByInvoice?: boolean;
  onFullCancel: (reason: string) => void;
  /** True once the warehouse has been told to fulfil (APPROVED/ACCEPTED/PACKED) — the backend then
   *  rejects a reasonless cancellation with CANCEL_REASON_REQUIRED, so the field is mandatory here. */
  reasonRequired?: boolean;
  /** Past approval, anyone who cannot DECIDE a cancellation is ASKING for one — the button and the
   *  copy have to say so, or people press "Cancel entire order" and wonder why it is still live. */
  needsApproval?: boolean;
  /** An ask already waiting on an Admin / Super Admin: there is nothing to do here but see where it stands. */
  pendingRequest?: { reason?: string | null; requestedAt?: string | null } | null;
  fullCancelLoading?: boolean;
  /** ORDER.CANCEL holder (retailer/sales/admin) + feature on + not blocked by a whole-order invoice. */
  canShortClose: boolean;
  onShortClose: (reason: string) => void;
  shortCloseLoading?: boolean;
  /** When short-close isn't available but units are still pending — the reason to show instead of the button. */
  shortCloseHint?: { message: string; description: string };
};

const n = (v: any) => Math.round((Number(v) || 0) * 1000) / 1000;

/**
 * Cancel confirmation for a (possibly partially fulfilled) order. Instead of a bare "are you sure",
 * it shows the fulfilment breakdown and steers the user to the only safe action: a full withdrawal
 * when nothing has shipped, or short-closing just the pending remainder once goods are out the door
 * (dispatched/delivered units can never be cancelled here).
 */
export default function CancelOrderModal(p: Props) {
  const [reason, setReason] = useState('');
  const { breakdown: b, fmtQty } = p;
  const shippedOut = n(b.dispatched + b.delivered);
  const pending = n(b.pending);
  const isPartial = shippedOut > 1e-6;

  /** Quantities here are UNITS across every line — a bare "60" beside another bare "60" said nothing. */
  const units = (v: number) => `${fmtQty(v)} unit${n(v) === 1 ? '' : 's'}`;
  const stat = (label: string, value: number, color?: string) => (
    <div style={{ minWidth: 88 }} key={label}>
      <div style={{ fontSize: 12, color: 'var(--gb-text-muted)' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color }}>{units(value)}</div>
    </div>
  );
  /** How much of the order has actually moved — nothing packed means "ordered" and "pending" are one fact. */
  const moved = n(b.packed + b.dispatched + b.delivered);

  const reasonMissing = !!p.reasonRequired && !reason.trim();

  let primary: JSX.Element | null = null;
  if (p.pendingRequest) {
    // Already asked: the only honest action left is to close the dialog.
    primary = null;
  } else if (!isPartial && p.canFullCancel) {
    primary = (
      <Button
        key="do"
        variant="danger"
        loading={p.fullCancelLoading}
        disabled={reasonMissing}
        onClick={() => p.onFullCancel(reason)}
      >
        {p.needsApproval ? 'Request cancellation' : 'Cancel entire order'}
      </Button>
    );
  } else if (isPartial && pending > 1e-6 && p.canShortClose) {
    primary = <Button key="do" variant="danger" loading={p.shortCloseLoading} onClick={() => p.onShortClose(reason)}>Short-close {fmtQty(pending)} pending</Button>;
  }

  return (
    <Modal
      open={p.open}
      onOpenChange={(o) => { if (!o) p.onClose(); }}
      title={`Cancel order ${p.orderNo}`}
      width={560}
      footer={[<Button key="close" variant="default" onClick={p.onClose}>{primary ? 'Keep order' : 'Close'}</Button>, primary]}
    >
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--gb-border)' }}>
        {moved <= 1e-6 ? (
          /*
           * Nothing has been packed, so "Ordered 60" and "Pending 60" were the SAME fact printed
           * twice with neither one naming its unit — which is exactly how it read: two unexplained
           * 60s. With nothing fulfilled there is only one number worth showing, and the sentence
           * says what the second one used to imply.
           */
          <div>
            <div style={{ fontSize: 12, color: 'var(--gb-text-muted)' }}>Order size</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{units(b.ordered)}</div>
            <div style={{ fontSize: 12, color: 'var(--gb-text-muted)', marginTop: 2 }}>
              Nothing packed or dispatched yet — all of it is still pending.
            </div>
          </div>
        ) : (
          <>
            {stat('Ordered', b.ordered)}
            {b.dispatched > 1e-6 && stat('Dispatched', b.dispatched, PALETTE.warning[600])}
            {b.delivered > 1e-6 && stat('Delivered', b.delivered, PALETTE.success[600])}
            {b.packed > 1e-6 && stat('Packed (awaiting dispatch)', b.packed, PALETTE.warning[600])}
            {/* "Pending" alone never said pending WHAT — these are the units not yet packed. */}
            {stat('Still to pack', b.pending, PALETTE.sky[500])}
          </>
        )}
      </div>

      {p.pendingRequest ? (
        /*
         * Someone already asked. Showing the cancel form again would offer an action the server will
         * refuse (CANCEL_ALREADY_REQUESTED) — so the dialog reports where the request stands instead.
         */
        <Alert
          type="info"
          title="A cancellation for this order is already waiting for an Admin / Super Admin to decide."
          description={
            <div>
              <div>{p.pendingRequest.reason ? `Reason given: “${p.pendingRequest.reason}”` : 'No reason was recorded.'}</div>
              <div style={{ marginTop: 6 }}>
                Until they decide, this order cannot be packed or dispatched. If it should go ahead after
                all, ask them to refuse the request.
              </div>
            </div>
          }
        />
      ) : isPartial ? (
        <>
          <Alert
            type="warning"
            style={{ marginBottom: 10 }}
            title={`${fmtQty(shippedOut)} unit(s) already dispatched — these cannot be cancelled.`}
            description="Goods that have left the warehouse can't be withdrawn. Only the units still pending (not yet dispatched) can be dropped."
          />
          {pending > 1e-6 ? (
            p.canShortClose ? (
              <>
                <Alert
                  type="info"
                  style={{ marginBottom: 10 }}
                  title={`Short-close the ${fmtQty(pending)} pending unit(s)?`}
                  description="They're removed from the order and the order total is revised to what was actually fulfilled. Settle any advance excess in Accounts (refund or keep as credit)."
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Reason (optional)</Typography.Text>
                <Input.TextArea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. retailer no longer needs the remaining units" style={{ marginTop: 4 }} />
              </>
            ) : p.shortCloseHint ? (
              <Alert type="info" title={p.shortCloseHint.message} description={p.shortCloseHint.description} />
            ) : null
          ) : (
            <Alert type="info" title="Everything ordered has been shipped — there's nothing left to cancel." />
          )}
        </>
      ) : p.canFullCancel ? (
        <>
          <Alert
            type="warning"
            title={p.needsApproval ? 'Ask for this order to be cancelled?' : 'Cancel the whole order?'}
            description={p.needsApproval
              // A request describes what the DECISION would do, and what happens meanwhile — the order
              // freezing is the part people need to know before they ask.
              ? `An Admin or Super Admin decides this one. Until they do, the order is frozen — it cannot be packed or dispatched.${
                b.packed > 1e-6 ? ` If they approve it, the ${fmtQty(b.packed)} packed unit(s) return to stock.` : ''}`
              : b.packed > 1e-6
                ? `This order hasn't been dispatched yet. Cancelling withdraws the entire order and returns the ${fmtQty(b.packed)} packed unit(s) to stock. This cannot be undone.`
                : "This order hasn't been packed or dispatched yet. Cancelling withdraws the entire order. This cannot be undone."}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 10 }}>
            {p.reasonRequired ? 'Reason (required)' : 'Reason (optional)'}
          </Typography.Text>
          <Input.TextArea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={p.reasonRequired
              ? 'e.g. retailer withdrew the order after approval'
              : 'e.g. duplicate order'}
            style={{ marginTop: 4 }}
          />
          {reasonMissing && (
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              The warehouse has already been told to fulfil this order — record why it is being cancelled.
            </Typography.Text>
          )}
        </>
      ) : p.fullCancelBlockedByInvoice ? (
        <Alert
          type="info"
          title="Void the tax invoice before cancelling."
          description="A Tally tax invoice has been recorded for this order. Void it in Billing (Documents tab) to reverse the ledger, then cancel the order."
        />
      ) : (
        <Alert type="info" title="This order can't be cancelled from here." />
      )}
    </Modal>
  );
}
